import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Collection, CollectionProducts } from 'src/app/models/collection.schema';
import { ProductCategory } from 'src/app/models/product-category.schema';
import { Product } from 'src/app/models/product.schema';
import { AuthStatusService } from 'src/app/services/auth-status.service';
import { CollectionService } from 'src/app/services/collection.service';
import { CommanUsesService } from 'src/app/services/comman-uses.service';
import { ProductCategoryService } from 'src/app/services/product-category.service';
import { ProductService } from 'src/app/services/product.service';
import { ProvincesService } from 'src/app/services/provinces.service';
import { SpinnerService } from 'src/app/services/spinner.service';

@Component({
  selector: 'app-shop-collection-form',
  templateUrl: './shop-collection-form.component.html',
  styleUrls: ['./shop-collection-form.component.scss']
})
export class ShopCollectionFormComponent implements OnInit {

  formValidationMsgs;
  collectionForm: any;
  groups: any;
  collectionId: number;
  collection: Collection;
  products: Product[] = [];
  allProducts: Product[] = [];
  provinces: any[] = [];
  imagePath: string;
  removeFile: boolean;
  selectAllProducts: boolean = false;
  isLoading: boolean = false;  
  btnDisabled: boolean = false;
  productCategories: ProductCategory[] = [];
  previousCategories: any[] = [];
  summerNoteConfig = {
    placeholder: '',
    tabsize: 2,
    height: '200px',
    uploadImagePath: '/',
    toolbar: [
      ['misc', ['codeview', 'undo', 'redo']],
      ['style', ['bold', 'italic', 'underline', 'clear', 'heading', 'style']],
      ['font', ['bold', 'italic', 'underline', 'strikethrough', 'superscript', 'subscript', 'clear']],
      ['fontsize', ['fontname', 'fontsize', 'color']],
      ['para', ['style', 'ul', 'ol', 'paragraph', 'height']],
      ['insert', ['table', 'link', 'hr']]
    ],
    fontNames: ['Helvetica', 'Arial', 'Arial Black', 'Comic Sans MS', 'Courier New', 'Roboto', 'Times']
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private productService: ProductService,
    private provincesService: ProvincesService,
    private authStatusService: AuthStatusService,
    private collectionService: CollectionService,
    private commanUsesService: CommanUsesService,
    private spinnerService: SpinnerService,
    private fb: FormBuilder,
    private productCategoryService: ProductCategoryService,
  ) { 
    try {
      this.groups = this.authStatusService.getRoles().map((elem: string) => elem.toLowerCase());
      this.formValidationMsgs = Collection.validationMessages;
    }
    catch {
      this.groups = [];
    }
  }

  async ngOnInit() {
    try {
      this.spinnerService.show();
      await this.initialize();
      this.collectionForm.get('shopCollectionProductCategories')?.valueChanges
      .subscribe((selectedCategories: any[]) => {
        this.onCategoryChange(selectedCategories);
      });
      await this.fetchProductsForCollection();
      await this.fetchProvinces();
      await this.fetchProductCategories();
      this.spinnerService.hide();
    } catch (error) {
      this.commanUsesService.showSnackBar(`SOMETHING_WENT_WRONG_${error?.message?.toUpperCase()}`);
      this.spinnerService.hide();
    }
  }

  async fetchProductsForCollection() {
    this.products = (await this.productService.fetchProductsForCollection()).products;
    this.allProducts = this.products;
  }

  async fetchProvinces() {
    this.provinces = await this.provincesService.getAllProvinces();
  }

  async fetchProductCategories() {
    this.productCategories = await this.productCategoryService.getAllProductCategories();
  }

  async initialize() {
    this.collectionForm = new FormGroup(Collection.validation());
    const collectionId = this.route.snapshot.paramMap.get('id');
    this.collectionId = Number(collectionId);
    if (this.collectionId) {
      try {
        this.collection = await this.collectionService.getCollection(this.collectionId);
      }
      catch {
        this.router.navigateByUrl("/404", { skipLocationChange: true });
      }
    }
    if (!this.collection) {
      this.collection = new Collection();
    } else {
      this.patch();
    }
  }

  patch() { 
    this.removeFile = false;
    [
      'collectionName',
      'bodyHtml',
    ].forEach(param => {
      this.collectionForm.controls[param].setValue(this.collection[param]);
    });
    if(this.collection.image){
      this.imagePath = this.collection.image;
    }
    if (this.collection.province){
      this.collectionForm.controls.province.setValue(this.collection.province.id);
    }

    if(this.collection?.collectionProducts?.length > 0){
      this.patchCollectionProducts();
    }

    if(this.collection?.shopCollectionProductCategories?.length > 0){
      this.collectionForm.patchValue({
        shopCollectionProductCategories: this.collection?.shopCollectionProductCategories.map((data) => {
          return data.productCategory.id
        })
      });
      this.previousCategories = [...this.collection?.shopCollectionProductCategories.map((data) => data.productCategory.id)];
    }
  }

  patchCollectionProducts(){
    const control = <FormArray>this.collectionForm.get('collectionProducts');
    control.clear();
    if (!this.collection.collectionProducts || Array.isArray(this.collection.collectionProducts) && this.collection.collectionProducts.length == 0){
      this.collection.collectionProducts = [new CollectionProducts()];
    }
    this.collection.collectionProducts.forEach((f, index) => {
      control.push(this.patchCollectionProductsValues(f, index));
    });
  }

  patchCollectionProductsValues(collectionProducts: CollectionProducts, index: number) {
    let collectionProduct = Collection.collectionProductFormGroup();
    collectionProduct.controls.product.setValue(
      { 
        id: collectionProducts?.product?.id, 
      }
    );
        
    return collectionProduct;
  }

  openImage(imageUrl: string) {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  }

  deleteFile() {
    this.removeFile = true;
  }

  addProduct() {
    const control = <FormArray>this.collectionForm.get('collectionProducts');
    control.push(Product.superProductFormGroup());
  }

  onFileSelect(event) {
    const file = (event.target as HTMLInputElement).files[0];
    if (file) {
      this.collectionForm.patchValue({
        image: file
      });
      this.collectionForm.get('image').updateValueAndValidity();
    }
  }

  get imageControl() {
    return this.collectionForm.get('image');
  }

  removeProduct(index: number) {
    const control = <FormArray>this.collectionForm.get('collectionProducts');
    if (control.length === 1) {
      return;
    }
    control.removeAt(index);
  }

  isProductOptionDisabled(item: any): boolean {
    let hasEmptyId = false;
    for (const products of this.collectionForm.value.collectionProducts) {
      if (products?.product?.id === item.id) {
        hasEmptyId = true;
      }
    }
    return hasEmptyId;
  }

  async onSubmit() {
    try {
      if(this.collectionForm.valid) {
        this.btnDisabled = true;
        let collectionValues = this.collectionForm.value;
        const formData = new FormData();
        
        if (this.collectionForm.get('image')?.value) {
          formData.append('image', this.collectionForm.get('image')?.value);
        }

        Object.keys(collectionValues).forEach(key => {
          if (key !== 'image' && key !== 'collectionProducts' && key !== 'shopCollectionProductCategories') { 
            formData.append(key, collectionValues[key]);
          }
        });

        if (collectionValues.collectionProducts && collectionValues.collectionProducts.length) {
          formData.append('collectionProducts', JSON.stringify(collectionValues.collectionProducts));
        }

        if (collectionValues.shopCollectionProductCategories && collectionValues.collectionProducts.length) {
          let categoryArray = [];
          for(let categoryId of collectionValues.shopCollectionProductCategories) {
            categoryArray.push({
              productCategory: {
                id: categoryId
              }
            })
          }
          formData.append('shopCollectionProductCategories', JSON.stringify(categoryArray));
        }

        if (this.collection.id) {
          formData.append('id', String(this.collection.id));
        }

        let promise = this.collectionService.createOrUpdateCollection(formData);
        /*
        if(this.collection?.id) {
          promise = this.collectionService.updateCollection(this.collection.id, formData);
        } else {
          promise = this.collectionService.createOrUpdateCollection(formData);
        }
        */
       const message = this.collection?.id ? 'COLLECTION_UPDATED_SUCCESSFULLY' : 'COLLECTION_CREATED_SUCCESSFULLY';
        promise.then(async (response: any) => {
          if(response?.id) {
            this.commanUsesService.showSnackBar(message);
            this.btnDisabled = false;
            this.router.navigate(['collections/edit', response?.id]);
            this.collection = response;
            this.patch();
          } else {
            this.btnDisabled = false;
            this.commanUsesService.showSnackBar("SOMETHING_WENT_WRONG");
          }
        }).catch(async (error) => {
          this.btnDisabled = false;
          const errorMessage = error?.error?.message || 'An error occurred while creating the collection. Please try again.';
          this.commanUsesService.showSnackBar(errorMessage);
        });
      } else {
        this.btnDisabled = false;
        this.commanUsesService.showSnackBar('PLEASE_FILL_ALL_REQUIRED_FIELDS.');
      }
    } catch (error) {
      this.btnDisabled = false;
      const errorMessage = error?.message || 'An unexpected error occurred. Please try again.';
      this.commanUsesService.showSnackBar(errorMessage);
    }
  }

  onCategoryChange(currentCategories: any[]) {
    const addedCategories = currentCategories.filter(category => !this.previousCategories.includes(category));
    const removedCategories = this.previousCategories.filter(category => !currentCategories.includes(category));
    if (addedCategories.length) {
      //console.log('Added categories:', addedCategories);
    }

    if (removedCategories.length) {
      //console.log('Removed categories:', removedCategories);
    }

    this.previousCategories = [...currentCategories];
    this.filterProducts();
  }

  handleProvinceChange(event: any) {
    try {
      let province = this.provinces.find((p) => p.id === event);
      this.filterProducts();
    } catch (error) {
      console.error(error);
    }
  }

  handleProductCategoryChange(event: any) {
    try {
      let productCategory = this.productCategories.find((c) => c.id === event);
      this.filterProducts();
    } catch (error) {
      console.error(error);
    }
  }

  filterProducts() {
    this.selectAllProducts = false;
    let provinceId = this.collectionForm.value.province;
    // let categoryId = this.collectionForm.value.productCategory;
    let categoryIds = this.collectionForm.value.shopCollectionProductCategories;

    this.toggleSelectAll();

    this.products = this.allProducts.filter((product) => {
      let matchesProvince = true;
      let matchesCategory = true;

      if (provinceId) {
        matchesProvince = product?.partner?.partnerProvinces?.some(
          (pProvince) =>
            pProvince?.province?.id === provinceId
        );
      }

      if (categoryIds && categoryIds.length > 0) {
        matchesCategory = categoryIds.some((categoryId) => 
          product?.productCategory?.id === categoryId
        );
      }

      return matchesProvince && matchesCategory;
    });

    if(!this.products.length)
      this.commanUsesService.showSnackBar('NO_PRODUCTS_FOUND');
  }


  createProductFormGroup(productId: number = null) {
    return this.fb.group({
      product: this.fb.group({
        id: [productId],
      }),
    });
  }

  toggleSelectAll() {
    this.spinnerService.show(); 
    const control = <FormArray>this.collectionForm.get('collectionProducts');
    
    if (this.selectAllProducts) {
      const formGroups: FormGroup[] = this.products.map((product) => {
        return this.fb.group({
          product: this.fb.group({
            id: [product.id], 
          }),
        });
      });

      control.clear();

      let counter = 0;
      const addFormGroups = () => {
        const batchSize = 50; 
        const end = Math.min(counter + batchSize, formGroups.length);

        for (let i = counter; i < end; i++) {
          control.push(formGroups[i]);
        }

        counter = end;

        if (counter < formGroups.length) {
          requestAnimationFrame(addFormGroups);
        } else {
          this.spinnerService.hide();
        }
      };

      requestAnimationFrame(addFormGroups); 
    } else {
      if (!this.products.length) {
        control.clear();
      } else {
        if (control.length > 1) {
          while (control.length > 1) {
            control.removeAt(1); 
          }
        }
      }
      this.spinnerService.hide();  
    }
  }


}
